import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useFeatureFlags } from 'Utilities/hooks';

import { FeesTable } from './FeesTable';

const PORTAL_TARGET_ID = 'seller-fee-table';
const OBSERVER_TIMEOUT_MS = 5000;

export const FeesTablePortal = () => {
  const { showServiceFee } = useFeatureFlags();
  const [portalTarget, setPortalTarget] = useState<Element | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!showServiceFee) {
      setPortalTarget(null);
      return;
    }

    const target = document.getElementById(PORTAL_TARGET_ID);
    if (target) {
      setPortalTarget(target);
      return;
    }

    const disconnect = () => {
      observerRef.current?.disconnect();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };

    const container = document.getElementById('main') ?? document.body;

    observerRef.current = new MutationObserver(() => {
      const el = document.getElementById(PORTAL_TARGET_ID);
      if (el) {
        setPortalTarget(el);
        disconnect();
      }
    });

    observerRef.current.observe(container, { childList: true, subtree: true });

    timeoutRef.current = setTimeout(() => {
      observerRef.current?.disconnect();
    }, OBSERVER_TIMEOUT_MS);

    // eslint-disable-next-line consistent-return
    return disconnect;
  }, [showServiceFee]);

  if (!showServiceFee || !portalTarget) {
    return null;
  }

  return createPortal(<FeesTable />, portalTarget);
};
